'use strict';

const baseSearch = require('base/search/search');
const pagination = require('./pagination');
const utils = require('../utils/index');
let focusedElementId;

/**
 * Update DOM elements with Ajax results
 *
 * @param {Object} $results - jQuery DOM element
 * @param {string} selector - DOM element to look up in the $results
 * @return {undefined}
 */
function updateDom($results, selector) {
	const $updates = $results.find(selector);
	$(selector).empty().html($updates.html());
}

/**
 * Keep refinement panes expanded/collapsed after Ajax refresh
 *
 * @param {Object} $results - jQuery DOM element
 * @return {undefined}
 */
function handleRefinements($results) {
	$('.refinement.active').each(function () {
		$(this).removeClass('active');
		let activeDiv = $results.find('.' + $(this)[0].className.replace(/ /g, '.'));
		activeDiv.addClass('active');
		activeDiv.find('button.title').attr('aria-expanded', 'true');
	});

	updateDom($results, '.refinements');
}

/**
 * Parse Ajax results and updated select DOM elements
 *
 * @param {string} response - Ajax response HTML code
 * @return {undefined}
 */
function parseResults(response) {
	const $results = $(response);
	const specialHandlers = {
		'.refinements': handleRefinements
	};

	// Update DOM elements that do not require special handling
	const domElementsToUpdate = [
		'.grid-header',
		'.header-bar',
		'.header.page-title',
		'.product-grid',
		'.show-more',
		'.filter-bar',
		'.breadcrumb-navigation',
		'.pageDescription',
		'.result-count'
	];

	domElementsToUpdate.forEach(function (selector) {
		updateDom($results, selector);
	});

	Object.keys(specialHandlers).forEach(function (selector) {
		specialHandlers[selector]($results);
	});
}

/**
 * Update sort option URLs from Ajax response
 *
 * @param {string} response - Ajax response HTML code
 * @return {undefined}
 */
function updateSortOptions(response) {
	var $tempDom = $('<div>').append($(response));
	var sortOptions = $tempDom.find('.grid-footer').data('sort-options').options;
	sortOptions.forEach(function (option) {
		$('option.' + option.id).val(option.url);
	});
}

/**
* Handles the category search results.
* If no results are found, it attempts to fetch the previous page's content.
*
* @param {string} response - The HTML response from the search request.
* @param {Event} event - The event object associated with the search action.
* @param {string} urlWithPagination - The URL used for fetching paginated results.
* @returns {Promise<Object>} A promise that resolves to an object containing:
*   - `result` {string}: The HTML content of the current or previous page.
*   - `showMessage` {boolean}: A flag indicating whether to show a message to the user.
*/
function handleCategoryResults(response, event, urlWithPagination) {
    const $results = $(response);
    const resultCount = parseInt($results.find('.js-search-result-count').text(), 10);

    if (resultCount === 0) {
        const previousUrl = sessionStorage.getItem('previousUrl');

        if (previousUrl) {
            return $.ajax({
                url: previousUrl,
                method: 'GET'
            }).then((previousPageContent) => {
                return {
                    result: previousPageContent,
                    showMessage: true
                };
            });
        }
    }

    return Promise.resolve({
        result: response,
        showMessage: false
    });
}

/**
* Stores the current URL of the window in the session storage.
* This function captures the current URL from the browser's address bar
* and saves it under the key 'previousUrl' in the session storage.
* It can be useful for tracking navigation history or implementing
* back-navigation features.
*/
function storeCurrentUrl() {
    const currentUrl = window.location.href;
    sessionStorage.setItem('previousUrl', currentUrl);
}

/**
* Stores the ID of the currently focused element in the document.
* If the focused element has an ID, it assigns it to the global variable `focusedElementId`.
* If the focused element does not have an ID, `focusedElementId` is set to null.
*/
function storeFocusedElement() {
    const $focusedElement = document.activeElement;

    if ($focusedElement) {
        focusedElementId = $focusedElement.id || null;
    }
}

/**
* Restores focus to a previously focused HTML element, if it exists.
* 
* This function checks if there is a stored `focusedElementId` and attempts to
* retrieve the corresponding DOM element using `document.getElementById`. If
* the element is found, it sets focus back to this element.
* 
* @global
* @returns {void} This function does not return a value.
*/
function restoreFocus() {
    if (focusedElementId) {
        var $elementToFocus = document.getElementById(focusedElementId);

        if ($elementToFocus) {
            $elementToFocus.focus();
        }
    }
}

module.exports = Object.assign(baseSearch, {
    filter: function () {
        $('.container').on('click', 'button.filter-results', function () {
            $('.refinement-bar, .modal-background').show();
            $(document).trigger('modalOpened');
            $('.refinement-bar').siblings().attr('aria-hidden', true);
            $('.refinement-bar').closest('.row').siblings().attr('aria-hidden', true);
            $('.refinement-bar').closest('.tab-pane.active').siblings().attr('aria-hidden', true);
            $('.refinement-bar').closest('.container.search-results').siblings().attr('aria-hidden', true);
            $('.refinement-bar .close').focus();
        });
    },
	applyFilter: function () {
		// Handle refinement value selection and reset click
		const ACTION = 'applyFilter';
		$('.container').on(
			'click',
			'.refinements li button, .refinements li label, .refinement-bar button.reset, .filter-value button, .swatch-filter button',
			function (e) {
				e.preventDefault();
				e.stopPropagation();

				$.spinner().start();
				$(this).trigger('search:filter', e);

				let urlWithPagination = pagination.getUrlWithPagination($(this).data('href'));
                const clickEvent = e;

                storeCurrentUrl();
                storeFocusedElement();

				$.ajax({
					url: urlWithPagination,
					data: {
						page: $('.grid-footer').data('page-number'),
						selectedUrl: $(this).data('href'),
						template: $('input[name="fallbackTemplate"]').val()
					},
					method: 'GET'
                }).then((response) => {
                    return handleCategoryResults(response, e, urlWithPagination);
                }).then((response) => {
                    parseResults(response.result);
                    pagination.addQueryToBrowserHistory(urlWithPagination, ACTION);

                    const $target = $(clickEvent.currentTarget);
                    const targetID = '#' + $target.closest('.card-body').attr('id');

                    if ($target.is('button.reset')) {
                        sessionStorage.removeItem('refinementState');
                    } else if ($target.hasClass('js-refinement-checkbox')) {
                        const $checkboxesContainer = $('.refinements').find(targetID);
                        const $changedRefinement = $checkboxesContainer.closest('.refinement').find('.js-custom-refinement-btn');
                        const refinementID = $changedRefinement.data('target').replace('#refinement-', '');
                        const areAllUnchecked = $checkboxesContainer.find('[data-selected="true"]').length === 0;

                        if ($target.data('selected') && !$changedRefinement.hasClass('default-active') && areAllUnchecked) {
                            $('body').trigger('search:beforeFilter', { changedRefinementID: refinementID, isExpanded: false });
                        } else if (!$target.data('selected')) {
                            $('body').trigger('search:beforeFilter', { changedRefinementID: refinementID, isExpanded: true });
                        }
                    }

                    $('body').trigger('search:filter');

                    const $message = $('.js-search-result-label');

                    if (response.showMessage) {
                        $message.show();
                    } else {
                        $message.hide();
                    }

                    $.spinner().stop();
                    $('.refinement-bar, .modal-background').hide();
                    restoreFocus();
                }).catch(() => {
                    $.spinner().stop();
                });
			}
		);
	},

	applyOnlineFilter: function () {
		$('.container').on(
			'click',
			'#online-toggle',
			function (e) {
				e.preventDefault();
				e.stopPropagation();

				$('.refinements li label').trigger('click');
			}
		);
	},

	showMore: function () {
		// Show more products
		const ACTION = 'showMore';
		$('.container').on('click', '.show-more button', function (e) {
			e.stopPropagation();
			var showMoreUrl = $(this).data('url');
			e.preventDefault();

			$.spinner().start();
			$(this).trigger('search:showMore', e);
			$.ajax({
				url: showMoreUrl,
				data: { selectedUrl: showMoreUrl },
				method: 'GET',
				success: function (response) {
					$('.grid-footer').replaceWith(response);
					updateSortOptions(response);
					pagination.addQueryToBrowserHistory(pagination.getUrlWithPagination(showMoreUrl), ACTION);
					$.spinner().stop();
				},
				error: function () {
					$.spinner().stop();
				}
			});
		});
	},

	sort: function () {
		// Handle sort order menu selection
		const ACTION = 'sort';
		$('.container').on('change', '[name=sort-order]', function (e) {
			e.preventDefault();

			$.spinner().start();
			$(this).trigger('search:sort', this.value);

			const url = this.value;

			$.ajax({
				url: url,
				data: { selectedUrl: this.value },
				method: 'GET',
				success: function (response) {
					$('.product-grid').empty().html(response);
					pagination.addQueryToBrowserHistory(pagination.getUrlWithPagination(url), ACTION);
					$.spinner().stop();
				},
				error: function () {
					$.spinner().stop();
				}
			});
		});
	},

	scrollPage: function () {
		// Handle moving the page to the last visited product when returning from the PDP
		let currentBrowserURL = new URL(window.location.href);
		let redirectGridUrl = $('input[name="redirectGridUrl"]').val();
		let lastViewProductID = utils.getCookie('pid');

		// apply the hash routing only when the back button was used and has a redirect url
		if (!redirectGridUrl === 'null' || redirectGridUrl === 'undefined') return;

		currentBrowserURL.hash = lastViewProductID;
		window.location.hash = lastViewProductID;
	}
});
